import { apiFetch, apiUploadFormData } from "@/lib/api";
import type { ApInvoicePurchaseOrderTracking } from "./apInvoiceTracking";

export type { ApInvoicePurchaseOrderTracking };

export type ApInvoiceStatus =
  | "draft"
  | "under_ai_review"
  | "awaiting_human_review"
  | "pending_tsh"
  | "submitted";

export type ApInvoicePurchaseKind = "po_inventory" | "non_po_purchase" | "standalone_tsh";

export type ApInvoiceTier = 0 | 1 | 2 | 3 | 4;

export type ApInvoiceStage =
  | "draft"
  | "without_receipt"
  | "receipt_discrepancy"
  | "receipt_not_approved"
  | "awaiting_gipi"
  | "pending_tsh"
  | "mirrored_to_gipi"
  | "non_po_ready"
  | "qbo_confirmed";

export const AP_INVOICE_DRAFT_VENDOR_NUMBER = "(draft)";

export type ApInvoiceReceiptMatchStatus =
  | "awaiting_receipt"
  | "matched"
  | "mismatch"
  | "matched_with_override";

export interface ApInvoiceLineRow {
  id: string;
  purchase_order_item_id: string | null;
  line_number: number;
  sku: string;
  description: string | null;
  quantity: string;
  unit_price: string | null;
  line_total: string | null;
  extracted_our_part_number: string | null;
  extracted_mpn: string | null;
  extracted_vendor_part_number: string | null;
  extracted_part_number: string | null;
  finished_good_sku: string | null;
  sku_match_method: string | null;
  qbo_expense_account_id: string | null;
  qbo_expense_account_name: string | null;
}

export type ApInvoiceDocumentKind = "vendor_pdf" | "email" | "other";

export interface ApInvoiceDocumentRow {
  id: string;
  kind: ApInvoiceDocumentKind;
  kind_label: string;
  filename: string;
  content_type: string;
}

export interface ApInvoiceRow {
  id: string;
  status: ApInvoiceStatus;
  status_label: string;
  purchase_kind: ApInvoicePurchaseKind;
  purchase_kind_label: string;
  merged_into_accounts_payable_invoice_id?: string | null;
  standalone_tsh_source_accounts_payable_invoice_id: string | null;
  /** Vendor invoice number of the linked stock PO vendor invoice (standalone TSH only). */
  standalone_tsh_source_vendor_invoice_number?: string | null;
  /** Megaventory payment terms enumeration stored on this invoice (null = not set). */
  payment_terms?: string | null;
  purchase_order_payment_terms?: string | null;
  supplier_payment_terms?: string | null;
  vendor_invoice_number: string;
  invoice_date: string | null;
  total_amount: string | null;
  currency: string;
  notes: string | null;
  comment_count: number;
  comment_preview: Array<{ text: string; author: string; created_at: string }>;
  supplier_id: string | null;
  supplier_name: string | null;
  purchase_order_id: string | null;
  purchase_order_number: string | null;
  inbound_receipt_id: string | null;
  receipt_status: string | null;
  receipt_match_status: ApInvoiceReceiptMatchStatus | null;
  receipt_match_status_label: string | null;
  purchase_invoice_id: string | null;
  purchase_invoice_mv_id: string | null;
  purchase_invoice_mv_number: string | null;
  purchase_invoice_mv_status: string | null;
  purchase_invoice_qb_id: string | null;
  qbo_match_confirmed_at: string | null;
  qbo_match_confirmed_by_name: string | null;
  invoice_file_filename: string | null;
  has_document: boolean;
  documents: ApInvoiceDocumentRow[];
  freshdesk_ticket_id: number | null;
  ai_review_requested_at: string | null;
  ai_review_completed_at: string | null;
  ai_review_agent_run_id: string | null;
  ai_review_error: string | null;
  ai_review_model_id: string | null;
  ai_review_input_tokens: string | null;
  ai_review_output_tokens: string | null;
  ai_review_cache_read_tokens: string | null;
  ai_review_cache_write_tokens: string | null;
  ai_review_estimated_cost_usd: string | null;
  human_review_approved_at: string | null;
  lines: ApInvoiceLineRow[];
  tier: ApInvoiceTier;
  stage: ApInvoiceStage;
  tier_label: string;
  stage_label: string;
  gipi_sync_status: string | null;
  gipi_sync_error: string | null;
  tsh_tariff_amount: string | null;
  tsh_shipping_amount: string | null;
  tsh_handling_amount: string | null;
  tsh_one_off_amount: string | null;
  tsh_amount: string | null;
  tsh_fee_description: string | null;
  tsh_support_ticket_id: string | null;
  tsh_workflow_status: string | null;
  tsh_workflow_status_label: string | null;
  tsh_parent_purchase_invoice_id: string | null;
  tsh_purchase_invoice_id: string | null;
  tsh_parent_mv_number: string | null;
  tsh_purchase_invoice_mv_number: string | null;
  tsh_purchase_invoice_mv_id: string | null;
  tsh_purchase_invoice_qb_id: string | null;
  tsh_linked_at: string | null;
  tsh_link_error: string | null;
  tsh_workflow: {
    shape: "none" | "mixed" | "standalone";
    status: string | null;
    parent_purchase_invoice_id: number | null;
    tsh_purchase_invoice_id: number | null;
    can_choose_parent: boolean;
    can_open_megaventory: boolean;
    is_complete: boolean;
  };
  tracking_numbers: string[];
  tracking_number: string | null;
  extracted_tracking_numbers: string[];
  purchase_order_tracking: ApInvoicePurchaseOrderTracking[];
  purchase_order_system_of_record: string | null;
  purchase_order_available_to_invoice: string | null;
  qbo_bill_id: string | null;
  qbo_gipi_bill_id: string | null;
  qbo_tsh_bill_id: string | null;
  qbo_bill_doc_number: string | null;
  qbo_vendor_id: string | null;
  qbo_bill_sync_status: string | null;
  qbo_bill_sync_error: string | null;
  qbo_bill_created_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ApInvoiceLinePatchInput {
  purchase_order_item_id?: number | null;
  sku: string;
  description?: string | null;
  quantity: number;
  unit_price?: number | null;
  line_total?: number | null;
  extracted_our_part_number?: string | null;
  extracted_mpn?: string | null;
  extracted_vendor_part_number?: string | null;
  extracted_part_number?: string | null;
  finished_good_sku?: string | null;
  sku_match_method?: string | null;
  qbo_expense_account_id?: string | null;
  qbo_expense_account_name?: string | null;
}

export interface PatchApInvoiceInput {
  vendor_invoice_number?: string;
  supplier_id?: number | null;
  purchase_order_id?: number | null;
  purchase_kind?: ApInvoicePurchaseKind;
  standalone_tsh_source_accounts_payable_invoice_id?: number | null;
  invoice_date?: string | null;
  total_amount?: number | null;
  payment_terms?: string | null;
  notes?: string | null;
  tsh_tariff_amount?: number | null;
  tsh_shipping_amount?: number | null;
  tsh_handling_amount?: number | null;
  tsh_one_off_amount?: number | null;
  tsh_amount?: number | null;
  tsh_fee_description?: string | null;
  tracking_numbers?: string[];
  tracking_number?: string | null;
  lines?: ApInvoiceLinePatchInput[];
}

export interface ApPurchaseOrderLinePrefillRow {
  id: string;
  sku: string;
  finished_good_sku?: string | null;
  description: string | null;
  quantity: string;
  unit_price: string | null;
}

export interface ApPurchaseOrderLinesResponse {
  supplier_id: string | null;
  supplier_name: string | null;
  currency: string;
  tracking: ApInvoicePurchaseOrderTracking[];
  lines: ApPurchaseOrderLinePrefillRow[];
}

export async function fetchApInvoices(
  search: string | null,
  tier: ApInvoiceTier | null,
  status: ApInvoiceStatus | null = null,
): Promise<ApInvoiceRow[]> {
  const params = new URLSearchParams();
  if (search != null && search.trim() !== "") {
    params.set("q", search.trim());
  }
  if (tier != null) {
    params.set("tier", String(tier));
  }
  if (status != null) {
    params.set("status", status);
  }
  const query = params.toString();
  const data = await apiFetch<{ rows: ApInvoiceRow[] }>(
    `/api/accounting/ap/invoices${query !== "" ? `?${query}` : ""}`,
  );
  return data.rows;
}

export async function fetchApInvoice(invoiceId: string): Promise<ApInvoiceRow> {
  const data = await apiFetch<{ invoice: ApInvoiceRow }>(
    `/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}`,
  );
  return data.invoice;
}

export async function createApInvoiceDraft(): Promise<ApInvoiceRow> {
  const data = await apiFetch<{ invoice: ApInvoiceRow }>("/api/accounting/ap/invoices/draft", {
    method: "POST",
  });
  return data.invoice;
}

export interface ApInvoiceManufacturerPartCheckLine {
  client_key: string;
  sku: string;
  description?: string | null;
  extracted_our_part_number?: string | null;
  extracted_mpn?: string | null;
  extracted_vendor_part_number?: string | null;
  extracted_part_number?: string | null;
}

export interface ApInvoiceManufacturerPartCheckResult {
  client_key: string;
  sku: string;
  extracted_mpn: string | null;
  sku_match_method: string | null;
}

export async function checkApInvoiceManufacturerParts(
  invoiceId: string,
  lines: ApInvoiceManufacturerPartCheckLine[],
): Promise<ApInvoiceManufacturerPartCheckResult[]> {
  const data = await apiFetch<{ lines: ApInvoiceManufacturerPartCheckResult[] }>(
    `/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/manufacturer-part-check`,
    {
      method: "POST",
      body: JSON.stringify({ lines }),
    },
  );
  return data.lines;
}

export async function patchApInvoice(invoiceId: string, input: PatchApInvoiceInput): Promise<ApInvoiceRow> {
  const data = await apiFetch<{ invoice: ApInvoiceRow }>(
    `/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(input),
    },
  );
  return data.invoice;
}

export async function submitApInvoice(invoiceId: string): Promise<ApInvoiceRow> {
  const data = await apiFetch<{ invoice: ApInvoiceRow }>(
    `/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/submit`,
    { method: "POST" },
  );
  return data.invoice;
}

export interface ApInvoiceMergeCandidate {
  id: string;
  vendor_invoice_number: string | null;
  invoice_date: string | null;
  total_amount: string | null;
  currency: string;
  status: string;
}

export async function fetchApInvoiceMergeCandidates(invoiceId: string): Promise<ApInvoiceMergeCandidate[]> {
  const data = await apiFetch<{ rows: ApInvoiceMergeCandidate[] }>(
    `/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/merge-candidates`,
  );
  return data.rows;
}

export interface MultiPoTshLineRow {
  id: string;
  description: string | null;
  amount: string;
  sku?: string;
  kind: "gipi" | "expense" | "remainder";
  purchase_order_id: string | null;
  purchase_order_number: string | null;
  purchase_invoice_id: string | null;
  purchase_invoice_mv_number: string | null;
  qbo_expense_account_id: string | null;
  qbo_expense_account_name: string | null;
  created_accounts_payable_invoice_id: string | null;
}

export interface MultiPoTshGipiCandidate {
  purchase_invoice_id: number;
  mv_number: string | null;
  purchase_order_id: number | null;
  purchase_order_number: string | null;
  supplier_name: string | null;
}

export interface MultiPoTshCreatedRow {
  purchase_order_id: string | null;
  purchase_order_number: string | null;
  purchase_invoice_id: string;
  mv_number: string | null;
  amount: string;
  created_accounts_payable_invoice_id: string | null;
}

export interface UpsInvoiceState {
  enabled: boolean;
  applied_at: string | null;
  invoice_total: string | null;
  lines: MultiPoTshLineRow[];
  created_tsh: MultiPoTshCreatedRow[];
  qbo_expense_account_id: string | null;
  qbo_expense_account_name: string | null;
}

export async function searchMultiPoTshGipis(search: string): Promise<MultiPoTshGipiCandidate[]> {
  const data = await apiFetch<{ candidates: MultiPoTshGipiCandidate[] }>(
    "/api/accounting/ap/ups/gipi-candidates",
    { query: { search } },
  );
  return data.candidates;
}

export async function fetchApInvoiceUps(invoiceId: string): Promise<UpsInvoiceState> {
  const data = await apiFetch<{ ups: UpsInvoiceState }>(
    `/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/ups`,
  );
  return data.ups;
}

export async function setApInvoiceUpsMode(invoiceId: string, enabled: boolean): Promise<{
  invoice: ApInvoiceRow;
  ups: UpsInvoiceState;
}> {
  return apiFetch(
    `/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/ups`,
    { method: "POST", body: JSON.stringify({ enabled }) },
  );
}

export async function saveApInvoiceUpsAllocation(
  invoiceId: string,
  input: {
    invoice_total: number;
    lines: Array<{
      description: string;
      amount: number;
      kind: "gipi" | "expense" | "remainder";
      purchase_order_id: number | null;
      purchase_invoice_id: number | null;
      qbo_expense_account_id: string | null;
      qbo_expense_account_name: string | null;
    }>;
    qbo_expense_account_id: string | null;
    qbo_expense_account_name: string | null;
  },
): Promise<{ invoice: ApInvoiceRow; ups: UpsInvoiceState }> {
  return apiFetch(`/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/ups`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function importApInvoiceCarrierBilling(
  invoiceId: string,
  file: { contentBase64?: string; documentId?: string },
): Promise<{ invoice: ApInvoiceRow; ups: UpsInvoiceState }> {
  const body =
    file.documentId != null && file.documentId !== ""
      ? { document_id: Number.parseInt(file.documentId, 10) }
      : { content_base64: file.contentBase64 ?? "" };
  return apiFetch(`/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/ups/import-csv`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function applyApInvoiceUpsAllocation(invoiceId: string): Promise<{
  invoice: ApInvoiceRow;
  ups: UpsInvoiceState;
}> {
  return apiFetch(`/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/ups/apply`, {
    method: "POST",
  });
}

export interface CbpTshSplitState {
  role: "none" | "source" | "cbp";
  amount: string | null;
  supplier_id: string | null;
  supplier_name: string | null;
  source_accounts_payable_invoice_id: string | null;
  source_vendor_invoice_number: string | null;
  created_accounts_payable_invoice_id: string | null;
  pms_release_on: string | null;
  pms_due_on: string | null;
}

export async function fetchApInvoiceCbpTsh(invoiceId: string): Promise<CbpTshSplitState> {
  const data = await apiFetch<{ cbp_tsh: CbpTshSplitState }>(
    `/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/cbp-tsh`,
  );
  return data.cbp_tsh;
}

export async function createApInvoiceCbpTsh(
  invoiceId: string,
  input: { amount: number; entry_date: string },
): Promise<CbpTshSplitState> {
  const data = await apiFetch<{ cbp_tsh: CbpTshSplitState }>(
    `/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/cbp-tsh`,
    { method: "POST", body: JSON.stringify(input) },
  );
  return data.cbp_tsh;
}

export async function mergeApInvoice(invoiceId: string, sourceInvoiceId: string): Promise<ApInvoiceRow> {
  const data = await apiFetch<{ invoice: ApInvoiceRow }>(
    `/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/merge`,
    {
      method: "POST",
      body: JSON.stringify({ source_invoice_id: Number.parseInt(sourceInvoiceId, 10) }),
    },
  );
  return data.invoice;
}

export async function reportApInvoiceAiReviewIssue(invoiceId: string, issue: string): Promise<{ id: string }> {
  return apiFetch<{ id: string }>(
    `/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/ai-review-issues`,
    {
      method: "POST",
      body: JSON.stringify({ issue }),
    },
  );
}

export async function deleteApInvoiceDraft(invoiceId: string): Promise<void> {
  await apiFetch<void>(`/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}`, {
    method: "DELETE",
  });
}

export interface ApPurchaseOrderVendorInvoice {
  id: string;
  vendor_invoice_number: string | null;
  invoice_date: string | null;
  total_amount: string | null;
  currency: string;
  status: string;
  purchase_invoice_mv_number: string | null;
  merged_into_accounts_payable_invoice_id: string | null;
  merge_eligible: boolean;
}

export async function fetchApPurchaseOrderVendorInvoices(
  purchaseOrderId: string,
  excludeInvoiceId: string,
): Promise<ApPurchaseOrderVendorInvoice[]> {
  const params = new URLSearchParams();
  if (excludeInvoiceId.trim() !== "") params.set("exclude", excludeInvoiceId);
  const query = params.toString();
  const data = await apiFetch<{ rows: ApPurchaseOrderVendorInvoice[] }>(
    `/api/accounting/ap/purchase-orders/${encodeURIComponent(purchaseOrderId)}/vendor-invoices${query === "" ? "" : `?${query}`}`,
  );
  return data.rows;
}

export async function fetchApPurchaseOrderLines(purchaseOrderId: string): Promise<ApPurchaseOrderLinesResponse> {
  return apiFetch<ApPurchaseOrderLinesResponse>(
    `/api/accounting/ap/purchase-orders/${encodeURIComponent(purchaseOrderId)}/lines`,
  );
}

export async function uploadApInvoiceDocument(invoiceId: string, file: File): Promise<ApInvoiceRow> {
  const formData = new FormData();
  formData.append("file", file);
  const data = await apiUploadFormData<{ invoice: ApInvoiceRow; document_id?: number }>(
    `/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/document`,
    formData,
  );
  return data.invoice;
}

export async function fetchApInvoiceDocumentUrl(
  invoiceId: string,
): Promise<{ signedUrl: string; filename: string | null }> {
  return apiFetch<{ signedUrl: string; filename: string | null }>(
    `/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/document`,
  );
}

export async function fetchApInvoiceDocumentById(
  invoiceId: string,
  documentId: string,
): Promise<{ signedUrl: string; filename: string | null; contentType: string | null }> {
  return apiFetch<{ signedUrl: string; filename: string | null; contentType: string | null }>(
    `/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/documents/${encodeURIComponent(documentId)}`,
  );
}

export async function fetchApInvoiceDocumentPreview(
  invoiceId: string,
  documentId: string,
): Promise<{ previewHtml: string; readableText: string; filename: string; contentType: string }> {
  return apiFetch<{ previewHtml: string; readableText: string; filename: string; contentType: string }>(
    `/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/documents/${encodeURIComponent(documentId)}/preview`,
  );
}

export interface ApInvoiceSpreadsheetPreviewResponse {
  sheets: Array<{ index: number; name: string }>;
  active_sheet_index: number;
  sheet: {
    index: number;
    name: string;
    header_row_index: number | null;
    columns: Array<{ key: string; label: string }>;
    rows: string[][];
  };
  truncated_rows: boolean;
  truncated_columns: boolean;
}

export async function fetchApInvoiceDocumentSpreadsheetPreview(
  invoiceId: string,
  documentId: string,
  sheetIndex = 0,
): Promise<ApInvoiceSpreadsheetPreviewResponse> {
  return apiFetch<ApInvoiceSpreadsheetPreviewResponse>(
    `/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/documents/${encodeURIComponent(documentId)}/preview-spreadsheet`,
    { query: sheetIndex > 0 ? { sheet: sheetIndex } : undefined },
  );
}

export async function deleteApInvoiceDocument(invoiceId: string, documentId: string): Promise<ApInvoiceRow> {
  const data = await apiFetch<{ invoice: ApInvoiceRow }>(
    `/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/documents/${encodeURIComponent(documentId)}`,
    { method: "DELETE" },
  );
  return data.invoice;
}

export async function retryApInvoiceAiReview(invoiceId: string): Promise<ApInvoiceRow> {
  const data = await apiFetch<{ invoice: ApInvoiceRow; message?: string }>(
    `/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/retry-ai-review`,
    { method: "POST" },
  );
  return data.invoice;
}

export async function startApInvoiceAiReview(invoiceId: string): Promise<ApInvoiceRow> {
  const data = await apiFetch<{ invoice: ApInvoiceRow; message?: string }>(
    `/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/ai-review`,
    { method: "POST" },
  );
  return data.invoice;
}

export async function returnApInvoiceToDraft(invoiceId: string): Promise<ApInvoiceRow> {
  const data = await apiFetch<{ invoice: ApInvoiceRow }>(
    `/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/return-to-draft`,
    { method: "POST" },
  );
  return data.invoice;
}

export async function resetApInvoiceToDraft(invoiceId: string): Promise<ApInvoiceRow> {
  const data = await apiFetch<{ invoice: ApInvoiceRow }>(
    `/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/reset-to-draft`,
    { method: "POST" },
  );
  return data.invoice;
}

export interface ApInvoiceQboDocHintBill {
  label: string;
  qbo_id: string | null;
  paid: boolean | null;
}

export interface ApInvoiceQboDocHint {
  verified_by_name: string | null;
  bills: ApInvoiceQboDocHintBill[];
  error: string | null;
}

export async function fetchApInvoiceQboDocHint(invoiceId: string): Promise<ApInvoiceQboDocHint> {
  return apiFetch<ApInvoiceQboDocHint>(
    `/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/qbo-doc-hint`,
  );
}

export async function confirmApInvoiceQboMatch(invoiceId: string): Promise<ApInvoiceRow> {
  const data = await apiFetch<{ invoice: ApInvoiceRow }>(
    `/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/confirm-qbo-match`,
    { method: "POST" },
  );
  return data.invoice;
}

/** Hub UI only — not for agents or npm tools. */
export const AP_INVOICE_QBO_BILL_UI_CLIENT_HEADER = "x-sgs-hub-ui-client";
export const AP_INVOICE_QBO_BILL_UI_CLIENT_VALUE = "accounting-ap-qbo-bill-v1";

function apInvoiceQboBillUiHeaders(): Record<string, string> {
  return {
    [AP_INVOICE_QBO_BILL_UI_CLIENT_HEADER]: AP_INVOICE_QBO_BILL_UI_CLIENT_VALUE,
  };
}

export interface ApInvoiceQboBillPrepareLine {
  id: string;
  sku: string;
  description: string | null;
  amount: number;
  qbo_expense_account_id: string | null;
  qbo_expense_account_name: string | null;
}

export interface ApInvoiceQboBillPrepareResponse {
  invoice: ApInvoiceRow;
  intent_token: string;
  intent_expires_at: string;
  suggested_vendor: { id: string; displayName: string } | null;
  lines: ApInvoiceQboBillPrepareLine[];
}

export async function prepareApInvoiceQboBill(invoiceId: string): Promise<ApInvoiceQboBillPrepareResponse> {
  return apiFetch<ApInvoiceQboBillPrepareResponse>(
    `/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/qbo-bill-prepare`,
    { method: "POST", headers: apInvoiceQboBillUiHeaders() },
  );
}

export async function createApInvoiceQboBill(
  invoiceId: string,
  input: {
    intent_token: string;
    qbo_vendor_id: string;
    lines: Array<{ line_id: string; qbo_account_id: string; qbo_account_name?: string | null }>;
  },
): Promise<{ invoice: ApInvoiceRow; qbo_bill_id: string; qbo_bill_doc_number: string }> {
  return apiFetch<{ invoice: ApInvoiceRow; qbo_bill_id: string; qbo_bill_doc_number: string }>(
    `/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/create-qbo-bill`,
    {
      method: "POST",
      headers: apInvoiceQboBillUiHeaders(),
      body: input,
    },
  );
}

export async function searchApQboVendors(query: string): Promise<
  Array<{ id: string; displayName: string; companyName?: string; active: boolean }>
> {
  const data = await apiFetch<{
    rows: Array<{ id: string; displayName: string; companyName?: string; active: boolean }>;
  }>("/api/accounting/ap/qbo-vendors", { query: { q: query } });
  return data.rows;
}

export async function searchApQboExpenseAccounts(query: string): Promise<
  Array<{ id: string; name: string; fullyQualifiedName: string; accountType: string }>
> {
  const data = await apiFetch<{
    rows: Array<{ id: string; name: string; fullyQualifiedName: string; accountType: string }>;
  }>("/api/accounting/ap/qbo-expense-accounts", { query: { q: query } });
  return data.rows;
}

export interface ApInvoiceTshParentCandidate {
  purchase_invoice_id: number;
  mv_id: number | null;
  mv_number: string | null;
  invoice_date: string | null;
  supplier_name: string | null;
  purchase_order_number: string | null;
  currency: string | null;
  total_amount: string | null;
  used_by_other_vendor_invoice: boolean;
}

export interface ApInvoiceTshMatchCandidate {
  purchase_invoice_id: number;
  mv_id: number | null;
  mv_number: string | null;
  mv_status: string | null;
  reference: string | null;
  currency: string | null;
  total_amount: string | null;
  parent_matches: boolean;
  reference_matches: boolean;
  supplier_matches: boolean;
  currency_matches: boolean;
  amount_matches: boolean;
  classification: "exact" | "review" | "reject";
}

export interface ApInvoiceTshHandoff {
  workflow_status: string | null;
  parent_gipi: {
    purchase_invoice_id: number;
    mv_id: number;
    mv_number: string;
    created_at: string | null;
  } | null;
  tsh_create_path: "parented" | "prior_month_gipi" | null;
  prior_month_tsh_message: string | null;
  vendor_invoice_reference: string | null;
  currency: string;
  tariff_amount: string | null;
  shipping_amount: string | null;
  one_off_amount: string | null;
  total_tsh_amount: string | null;
  megaventory_url: string | null;
  gipi_targets?: Array<{
    purchase_invoice_id: number;
    mv_number: string;
    amount: string;
    megaventory_url: string | null;
    tsh_create_path: "parented" | "prior_month_gipi" | null;
  }>;
  candidate_tsh_documents: ApInvoiceTshMatchCandidate[];
}

export interface ApInvoiceStandaloneTshSourceCandidate {
  id: string;
  vendor_invoice_number: string;
  purchase_order_number: string | null;
  purchase_invoice_mv_number: string | null;
  stock_supplier_name: string | null;
  status: string;
}

export async function fetchApInvoiceStandaloneTshSourceCandidates(
  invoiceId: string,
  search?: string | null,
): Promise<ApInvoiceStandaloneTshSourceCandidate[]> {
  const query =
    search != null && search.trim() !== ""
      ? `?search=${encodeURIComponent(search.trim())}`
      : "";
  const data = await apiFetch<{ candidates: ApInvoiceStandaloneTshSourceCandidate[] }>(
    `/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/standalone-tsh-source-candidates${query}`,
  );
  return data.candidates;
}

export async function fetchApInvoiceTshParentCandidates(
  invoiceId: string,
  search?: string | null,
  purchaseOrderNumber?: string | null,
): Promise<{ candidates: ApInvoiceTshParentCandidate[]; gipiCount: number | null }> {
  const params = new URLSearchParams();
  const purchaseOrder = purchaseOrderNumber?.trim() ?? "";
  if (purchaseOrder !== "") {
    params.set("purchase_order", purchaseOrder);
  } else if (search != null && search.trim() !== "") {
    params.set("search", search.trim());
  }
  const query = params.size > 0 ? `?${params.toString()}` : "";
  const data = await apiFetch<{
    candidates: ApInvoiceTshParentCandidate[];
    gipi_count?: number | null;
  }>(
    `/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/tsh-parent-candidates${query}`,
  );
  return {
    candidates: data.candidates,
    gipiCount: purchaseOrder !== "" ? (data.gipi_count ?? data.candidates.length) : null,
  };
}

export async function setApInvoiceTshParent(
  invoiceId: string,
  parentPurchaseInvoiceId: number | null,
): Promise<Partial<ApInvoiceRow> & Pick<ApInvoiceRow, "id">> {
  const data = await apiFetch<{ invoice: Partial<ApInvoiceRow> & Pick<ApInvoiceRow, "id"> }>(
    `/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/tsh-parent`,
    {
      method: "POST",
      body: JSON.stringify({ parent_purchase_invoice_id: parentPurchaseInvoiceId }),
    },
  );
  return data.invoice;
}

export async function createApInvoicePriorMonthGipi(invoiceId: string): Promise<{
  invoice: ApInvoiceRow;
  handoff: ApInvoiceTshHandoff;
}> {
  return apiFetch<{ invoice: ApInvoiceRow; handoff: ApInvoiceTshHandoff }>(
    `/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/tsh/prior-month-gipi`,
    { method: "POST" },
  );
}

export async function openApInvoiceTshMegaventory(
  invoiceId: string,
  purchaseInvoiceId?: number | null,
): Promise<{
  invoice: ApInvoiceRow;
  handoff: ApInvoiceTshHandoff;
}> {
  return apiFetch<{ invoice: ApInvoiceRow; handoff: ApInvoiceTshHandoff }>(
    `/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/tsh/open-megaventory`,
    {
      method: "POST",
      body: JSON.stringify(
        purchaseInvoiceId != null ? { purchase_invoice_id: purchaseInvoiceId } : {},
      ),
    },
  );
}

export async function fetchApInvoiceTshHandoff(invoiceId: string): Promise<ApInvoiceTshHandoff> {
  const data = await apiFetch<{ handoff: ApInvoiceTshHandoff }>(
    `/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/tsh-match-candidates`,
  );
  return data.handoff;
}

export async function linkApInvoiceTshPurchaseInvoice(
  invoiceId: string,
  tshPurchaseInvoiceId: number,
): Promise<{ invoice: ApInvoiceRow; handoff: ApInvoiceTshHandoff }> {
  return apiFetch<{ invoice: ApInvoiceRow; handoff: ApInvoiceTshHandoff }>(
    `/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/tsh-link`,
    {
      method: "POST",
      body: JSON.stringify({ tsh_purchase_invoice_id: tshPurchaseInvoiceId }),
    },
  );
}

export interface ApInvoiceSplitPreviewResponse {
  mode: "vendor" | "manufacturer";
  suggestion: {
    groups: Array<{
      invoice_key: string | null;
      suggested_mfr_invoice_number: string | null;
      document_ids: string[];
      filenames: string[];
    }>;
    unassigned_document_ids: string[];
    duplicate_document_ids: string[];
    suggested_invoice_count: number;
    warnings: string[];
  };
  duplicate_mfr_warnings: string[];
  duplicate_mfr_invoice_numbers: string[];
}

export async function previewApInvoiceSplit(
  invoiceId: string,
  mode: "vendor" | "manufacturer",
): Promise<ApInvoiceSplitPreviewResponse> {
  return apiFetch<ApInvoiceSplitPreviewResponse>(
    `/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/split/preview`,
    {
      method: "POST",
      body: JSON.stringify({ mode }),
    },
  );
}

export async function splitApInvoiceIntoVendorDrafts(
  invoiceId: string,
  groups: Array<{
    document_ids: string[];
    vendor_invoice_number?: string | null;
    invoice_date?: string | null;
  }>,
): Promise<{ created_invoice_ids: number[]; remaining_invoice_id: number | null }> {
  return apiFetch<{ created_invoice_ids: number[]; remaining_invoice_id: number | null }>(
    `/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/split`,
    {
      method: "POST",
      body: JSON.stringify({ groups }),
    },
  );
}

export interface ApInvoiceManufacturerMigrateResponse {
  result: {
    manufacturer_invoice_id: number;
    manufacturer: string;
    mfr_invoice_number: string;
  };
  apStoragePaths: Array<{ storage_bucket: string; storage_path: string }>;
}

export async function migrateApInvoiceToManufacturer(
  invoiceId: string,
  input: { manufacturer_intent?: boolean; document_ids?: string[] },
): Promise<ApInvoiceManufacturerMigrateResponse> {
  return apiFetch<ApInvoiceManufacturerMigrateResponse>(
    `/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/migrate-manufacturer`,
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );
}

export async function migrateApInvoiceManufacturerBatch(
  invoiceId: string,
  input: {
    manufacturer_intent?: boolean;
    groups: Array<{
      document_ids: string[];
      mfr_invoice_number: string;
      date: string;
      total_cost?: number | null;
      remove_duplicate?: boolean;
    }>;
  },
): Promise<{
  results: ApInvoiceManufacturerMigrateResponse["result"][];
  remaining_document_count: number;
  vendor_invoice_id: number | null;
}> {
  return apiFetch<{
    results: ApInvoiceManufacturerMigrateResponse["result"][];
    remaining_document_count: number;
    vendor_invoice_id: number | null;
  }>(
    `/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/migrate-manufacturer-batch`,
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );
}
